
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../base/Button';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const { t, i18n } = useTranslation();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsLanguageOpen(false);
  };

  const languages = [
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  return (
    <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100">
      <nav className="px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              XINRUNMODA
            </h1>
          </div>
          
          <div className="hidden md:flex items-center space-x-8">
            <button 
              onClick={() => scrollToSection('about')}
              className="text-gray-700 hover:text-black transition-colors cursor-pointer"
            >
              {t('aboutUs')}
            </button>
            <button 
              onClick={() => scrollToSection('services')}
              className="text-gray-700 hover:text-black transition-colors cursor-pointer"
            >
              {t('services')}
            </button>
            <button 
              onClick={() => scrollToSection('brands')}
              className="text-gray-700 hover:text-black transition-colors cursor-pointer"
            >
              {t('brands')}
            </button>
            <button 
              onClick={() => scrollToSection('insights')}
              className="text-gray-700 hover:text-black transition-colors cursor-pointer"
            >
              {t('marketInsights')}
            </button>
            
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                className="flex items-center space-x-2 text-gray-700 hover:text-black transition-colors cursor-pointer"
              >
                <span>{currentLanguage.flag}</span>
                <span>{currentLanguage.name}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${isLanguageOpen ? 'rotate-180' : ''}`}></i>
              </button>
              
              {isLanguageOpen && (
                <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-[120px]">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-2 cursor-pointer ${
                        i18n.language === lang.code ? 'bg-gray-50 text-black' : 'text-gray-700'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <Button 
              variant="primary" 
              size="sm"
              onClick={() => scrollToSection('contact')}
            >
              {t('contact')}
            </Button>
          </div>
          
          <button 
            className="md:hidden w-6 h-6 flex items-center justify-center cursor-pointer"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>
        
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-100">
            <div className="flex flex-col space-y-4 pt-4">
              <button 
                onClick={() => scrollToSection('about')}
                className="text-left text-gray-700 hover:text-black transition-colors cursor-pointer"
              >
                {t('aboutUs')}
              </button>
              <button 
                onClick={() => scrollToSection('services')}
                className="text-left text-gray-700 hover:text-black transition-colors cursor-pointer"
              >
                {t('services')}
              </button>
              <button 
                onClick={() => scrollToSection('brands')}
                className="text-left text-gray-700 hover:text-black transition-colors cursor-pointer"
              >
                {t('brands')}
              </button>
              <button 
                onClick={() => scrollToSection('insights')}
                className="text-left text-gray-700 hover:text-black transition-colors cursor-pointer"
              >
                {t('marketInsights')}
              </button>
              
              {/* Mobile Language Selector */}
              <div className="border-t border-gray-100 pt-4">
                <div className="text-sm text-gray-500 mb-2">语言 / Language / Lingua</div>
                <div className="flex flex-wrap gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`px-3 py-1 rounded-full text-sm flex items-center space-x-1 cursor-pointer ${
                        i18n.language === lang.code 
                          ? 'bg-black text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              <Button 
                variant="primary" 
                size="sm"
                onClick={() => scrollToSection('contact')}
                className="self-start"
              >
                {t('contact')}
              </Button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
